import { Popconfirm } from "antd";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import ButtonLoader from "@/Components/ButtonLoader";
import { CheckIcon } from "@/Components/Icons/CheckIcon";
import { POUND_SIGN } from "@/Constants/common";
import { routingConstants } from "@/Constants/routingConstants";
import {
  resetPage,
  updateActiveStep,
  updateJobData,
  updateSetAsTemplate,
  deSelectShortListAllCandidate,
  fetchJobTemplates,
  toggleInvitedDirectBankCandidateForContractModal,
  updateFilters,
  updatePagination,
} from "@/reducers/clientcreatejob";
import { _createJob } from "@/services/createjob";
import { createJobValidations } from "@/utilities/CommonText";
import { defaultVariousTime } from "@/utilities/defaultData";
import {
  handleAPICatch,
  showConfirmToast,
  showErrorToast,
} from "@/utilities/utils";

import { deSelectAllCandidate } from "../../../../../reducers/client_candidates/candidatesinvited";
import { apiEncrypt } from "../../../../../utilities/keyEncryption";
import { SelectRemoveIcon } from "../../../../Icons/SelectRemoveIcon";
import ResetButton from "../buttons/ResetButton";
import { JobShiftLists } from "../JobShiftLists";
import { JobSteps } from "../JobSteps";
import { Summary } from "../Summary";

/**
 * This function represents the Verification component, which displays and confirms job details.
 *
 * @return {JSX} The JSX for the Verification component
 */
const Verification = () => {
  const {
    jobData,
    skillsList,
    candidateRoleList,
    jobShiftObj,
    getDayWiseApiLoading,
    shortlistCandidateData,
    invitedBankCandidateForContractModal,
    isUnitsValid,
    contractTemplates,
    selectedCandidateIds,
    pagination,
    activeTemplate,
  } = useSelector((state) => state.clientCreateJob);
  const { user } = useSelector((state) => state.clientProfile);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [skills, setSkills] = useState([]);
  const [createJobLoading, setCreateJobLoading] = useState(false);
  const [variousTime, setVariousTime] = useState(defaultVariousTime);

  useEffect(() => {
    setVariousTime(defaultVariousTime);
    if (jobShiftObj.length) {
      let shiftStartTime = jobShiftObj[0].startTime;
      let shiftEndTime = jobShiftObj[0].endTime;
      let shiftBreakTime = jobShiftObj[0].break;
      let shiftAdminTime = jobShiftObj[0].admin;
      jobShiftObj.forEach((item) => {
        if (shiftStartTime !== item.startTime) {
          setVariousTime((prev) => {
            return { ...prev, startTime: true };
          });
        }
        if (shiftEndTime !== item.endTime) {
          setVariousTime((prev) => {
            return { ...prev, endTime: true };
          });
        }
        if (shiftBreakTime !== item.break) {
          setVariousTime((prev) => {
            return { ...prev, breakTime: true };
          });
        }
        if (shiftAdminTime !== item.admin) {
          setVariousTime((prev) => {
            return { ...prev, adminTime: true };
          });
        }
      });

      let isStartTimeSame = jobShiftObj.every((item) => {
        return item.startTime === jobShiftObj[0].startTime;
      });
      let isEndTimeSame = jobShiftObj.every((item) => {
        return item.endTime === jobShiftObj[0].endTime;
      });
      if (isStartTimeSame && isEndTimeSame) {
        dispatch(
          updateJobData({
            ...jobData,
            startTime: jobShiftObj[0].startTime,
            endTime: jobShiftObj[0].endTime,
          }),
        );
      }
    } else {
      // dispatch(updateActiveStep(2))
    }
  }, [jobShiftObj]);
  /**
   * Retrieves a list of skills based on the provided jobData.
   *
   * @return {Array} skills list
   */
  const getSkillsList = () => {
    let skillsID = jobData.skills;
    let skills = [];
    skillsID.forEach((item) => {
      const skill = skillsList.find((skill) => skill.id === item);
      skills.push(skill?.name);
    });
    return skills;
  };
  useEffect(() => {
    setSkills(getSkillsList());
  }, [jobData.skills]);
  /**
   * Retrieves the role based on the jobData role ID.
   *
   * @return {string} The name of the role, or "-" if not found.
   */
  const getRole = () => {
    const role = candidateRoleList.find((item) => item.id === jobData.roleId);
    return role?.name || "-";
  };
  /**
   * Asynchronous function to handle job creation.
   *
   * @param {void} - No parameters
   * @return {void} - No return value
   */
  const handleCreateJob = async () => {
    let hasInvalidUnit = jobShiftObj.some((item) => {
      return item.isUnitInvalid;
    });

    if (hasInvalidUnit) {
      showErrorToast(createJobValidations.inValidUnit, {
        id: "inValidUnit",
      });
      setCreateJobLoading(false);
      return;
    }
    let data = {
      ...jobData,
      all_sub_contracts: jobShiftObj.filter((item) => {
        return item.rate_values.length > 0;
      }),
      custom_template_id:
        activeTemplate?.template_type == "normal" ? activeTemplate?.id : "",
      generic_template_id:
        activeTemplate?.template_type == "generic" ? activeTemplate?.id : "",
    };

    if (data.all_sub_contracts.length === 0) {
      showErrorToast(createJobValidations.emptyShifts, {
        id: "emptyShifts",
      });
      setCreateJobLoading(false);
      return;
    }
    let response = _createJob(data);
    response
      .then((res) => {
        dispatch(fetchJobTemplates());

        const {
          data: { contract_id, contract_ref_no, message },
        } = res;
        dispatch(resetPage());

        showConfirmToast((t) => {
          return (
            <>
              <div className="flex flex-col gap-4 border-0 p-1">
                <span className="text-[14px] leading-6 text-black">
                  {message} with Ref No: <b>{contract_ref_no}</b>
                </span>
                <div className="flex gap-2">
                  <button
                    className="transition-linear inline-block rounded-lg border border-primary bg-primary px-4 py-2 text-[12px] font-semibold text-white hover:bg-white hover:text-primary"
                    onClick={() => {
                      t.dismiss(t.id);
                      navigate(
                        routingConstants.contractDetail(
                          apiEncrypt(contract_id),
                        ),
                      );
                      // window.location = routingConstants.contractDetail(contract_id)
                    }}
                  >
                    View Contract
                  </button>
                  {/* <button onClick={() => toast.dismiss(t.id)} className="bg-red transition-linear px-4 py-2 border border-red hover:bg-white hover:text-red text-white inline-block text-[12px] rounded-lg font-bold">close</button> */}
                  <button
                    onClick={() => t.dismiss(t.id)}
                    className="transition-linear inline-block rounded-lg border border-red bg-white px-4 py-2 text-[12px] font-semibold text-red hover:bg-red hover:text-white"
                  >
                    Close
                  </button>
                </div>
              </div>
            </>
          );
        });
      })
      .catch((error) => {
        handleAPICatch(error);
      })
      .finally(() => {
        setCreateJobLoading(false);
      });
  };
  //   const getTotalCharge = () =>{
  //     return jobData.totalCandidatePay + jobData.applocumServiceFee;
  //   }
  return (
    <div className="Verification_Wrapper">
      <div className="grid gap-4">
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-[1fr,280px] xl:grid-cols-[1fr,330px]">
          <div className="grid h-fit gap-4">
            <JobSteps />
            <div className="rounded-lg bg-white p-6">
              <div className="mb-2 flex justify-between">
                <h4 className="text-[18px] font-medium leading-[normal] text-black">
                  Review and confirm your job details
                </h4>
                <div className="flex gap-3">
                  <div className="flex gap-4">
                    <ResetButton />
                  </div>
                  <button
                    type="button"
                    className={`ease flex h-[40px] cursor-pointer items-center gap-2 rounded-lg px-4 leading-[40px] transition-all duration-300 ${
                      jobData.setAsTemplate
                        ? "bg-primary text-white"
                        : "bg-grey text-txtColor hover:bg-lightblue"
                    }`}
                    role="checkbox"
                    tabIndex="0"
                    onClick={() => {
                      if (jobData.setAsTemplate) {
                        dispatch(updateSetAsTemplate(false));
                      } else {
                        dispatch(updateSetAsTemplate(true));
                      }
                    }}
                  >
                    <span
                      className={`block overflow-hidden !text-white transition-all duration-300 ${
                        jobData.setAsTemplate ? "w-[14px]" : "w-0"
                      }`}
                    >
                      <CheckIcon />
                    </span>
                    <span className="whitespace-nowrap text-[14px]">
                      {activeTemplate
                        ? "Update The Template"
                        : "Set as Template"}
                    </span>
                  </button>
                </div>
              </div>
              <div className="grid gap-6">
                <div className="grid grid-cols-4 border-b border-b-lightblue pb-4">
                  <div className="grid gap-3">
                    <h5 className="text-[14px] font-semibold leading-[normal] text-txtColor">
                      Job Title
                    </h5>
                    <span className="text-[14px] font-medium leading-[normal] text-black">
                      {jobData && jobData.jobTitle}
                    </span>
                  </div>
                  <div className="grid gap-3">
                    <h5 className="text-[14px] font-semibold leading-[normal] text-txtColor">
                      Role
                    </h5>
                    <span className="text-[14px] font-medium leading-[normal] text-black">
                      {getRole()}
                    </span>
                  </div>
                  <div className="grid gap-3">
                    <h5 className="text-[14px] font-semibold leading-[normal] text-txtColor">
                      Date
                    </h5>
                    <span className="text-[14px] font-medium leading-[normal] text-black">
                      {jobData && jobData.startDate} -{" "}
                      {jobData && jobData.endDate}
                    </span>
                  </div>
                  <div className="grid gap-3">
                    <h5 className="text-[14px] font-semibold leading-[normal] text-txtColor">
                      Total Charge
                    </h5>
                    <span className="text-[14px] font-bold leading-[normal] text-green">
                      {POUND_SIGN}{" "}
                      {(jobData.totalCost &&
                        parseFloat(jobData.totalCost).toFixed(2)) ||
                        "0.00"}
                    </span>
                  </div>
                </div>
                <div className="grid gap-3 border-b border-b-lightblue pb-4">
                  <h5 className="text-[14px] font-semibold leading-[normal] text-txtColor">
                    Job Description
                  </h5>
                  <p className="break-all text-[14px] font-medium leading-[normal] text-black">
                    {jobData && jobData.jobDescription}
                  </p>
                </div>
                <div className="grid gap-3 border-b border-b-lightblue pb-4">
                  <h5 className="text-[14px] font-semibold leading-[normal] text-txtColor">
                    Skills
                  </h5>
                  <div className="flex flex-wrap gap-[6px] text-[12px] font-normal text-black">
                    {skills.map((item, index) => {
                      return (
                        <span
                          className="inline-block rounded bg-lightblue p-[3px_12px] text-primary"
                          key={index}
                        >
                          {item}
                        </span>
                      );
                    })}
                  </div>
                </div>
                <div className="grid grid-cols-4 border-b border-b-lightblue pb-4">
                  <div className="grid gap-3">
                    <h5 className="text-[14px] font-semibold leading-[normal] text-txtColor">
                      Time
                    </h5>
                    <span className="text-[14px] font-medium leading-[normal] text-black">
                      {variousTime.startTime || variousTime.endTime ? (
                        "Various Times"
                      ) : (
                        <>
                          {jobData &&
                            jobData.startTime &&
                            moment(
                              "01/01/2007 " + jobData.startTime,
                              "DD/MM/YYYY HH:mm",
                            ).format("HH:mm")}
                          &nbsp;-&nbsp;
                          {jobData &&
                            jobData.endTime &&
                            moment(
                              "01/01/2007 " + jobData.endTime,
                              "DD/MM/YYYY HH:mm",
                            ).format("HH:mm")}
                        </>
                      )}
                    </span>
                  </div>
                  <div className="grid gap-3">
                    <h5 className="text-[14px] font-semibold leading-[normal] text-txtColor">
                      Break Time
                    </h5>
                    <span className="text-[14px] font-medium leading-[normal] text-black">
                      {variousTime.breakTime ? (
                        "Various Times"
                      ) : (
                        <>
                          {jobData &&
                          jobShiftObj.length &&
                          jobData.breakTime !== jobShiftObj[0].break ? (
                            jobShiftObj[0].break
                          ) : (
                            <>
                              {jobData && jobData.breakTime
                                ? moment(
                                    "01/01/2007 " + jobData.breakTime,
                                    "DD/MM/YYYY HH:mm",
                                  ).format("HH:mm")
                                : "0"}{" "}
                              Min
                            </>
                          )}
                        </>
                      )}
                    </span>
                  </div>
                  <div className="grid gap-3">
                    <h5 className="text-[14px] font-semibold leading-[normal] text-txtColor">
                      Admin Time
                    </h5>
                    <span className="text-[14px] font-medium leading-[normal] text-black">
                      {variousTime.adminTime ? (
                        "Various Times"
                      ) : (
                        <>
                          {jobData &&
                          jobShiftObj.length &&
                          jobData.adminTime !== jobShiftObj[0].admin ? (
                            jobShiftObj[0].admin
                          ) : (
                            <>
                              {jobData && jobData.adminTime
                                ? moment(
                                    "01/01/2007 " + jobData.adminTime,
                                    "DD/MM/YYYY HH:mm",
                                  ).format("HH:mm")
                                : "0"}{" "}
                              Min
                            </>
                          )}
                        </>
                      )}
                    </span>
                  </div>
                  <div className="grid gap-3">
                    <h5 className="text-[14px] font-semibold leading-[normal] text-txtColor">
                      No of Visits
                    </h5>
                    <span className="text-[14px] font-medium leading-[normal] text-black">
                      {jobData && jobData.noOfVisit ? jobData.noOfVisit : "0"}
                    </span>
                  </div>
                </div>
                <div className="flex justify-between gap-4">
                  <div className="flex w-full items-center justify-center gap-4">
                    <button
                      className="ease relative h-[40px] min-w-[200px] rounded-lg border border-primary bg-white px-2 text-[14px] font-medium leading-[38px] text-primary transition-all duration-200 hover:bg-none hover:bg-primary hover:text-white"
                      onClick={() => {
                        dispatch(updateFilters({ search: "" }));
                        dispatch(
                          updatePagination({
                            ...pagination,
                            currentPage: 1,
                            isLastPage: false,
                          }),
                        );

                        dispatch(
                          toggleInvitedDirectBankCandidateForContractModal(
                            true,
                          ),
                        );
                      }}
                      type="button"
                    >
                      <span>Shortlist Candidate</span>
                    </button>

                    <div className="flex w-full items-center gap-3">
                      {selectedCandidateIds?.length ? (
                        <>
                          <span className="font-medium">
                            Shortlist Candidate Name :
                          </span>
                          <div className="flex gap-2 rounded-md bg-lightblue p-[6px_20px] text-[14px] font-semibold leading-[normal] text-primary">
                            <>
                              <span className="text-[14px] font-bold text-txtColor">
                                {shortlistCandidateData
                                  ?.filter((candidate) =>
                                    selectedCandidateIds.includes(
                                      candidate.candidate_id,
                                    ),
                                  )
                                  ?.map((candidate) => candidate.candidate_name)
                                  ?.join(" , ")}
                              </span>
                              <button
                                onClick={() =>
                                  dispatch(deSelectShortListAllCandidate())
                                }
                              >
                                <SelectRemoveIcon />
                              </button>
                            </>
                          </div>
                        </>
                      ) : null}
                    </div>
                  </div>
                  <div className="flex gap-4">
                    <button
                      className="ease relative h-[40px] w-[120px] rounded-lg border border-primary bg-white text-[14px] font-medium leading-[38px] text-primary transition-all duration-200 hover:bg-none hover:bg-primary hover:text-white"
                      onClick={() => {
                        dispatch(updateActiveStep(3));
                      }}
                      type="button"
                    >
                      <span>Previous</span>
                    </button>
                    <Popconfirm
                      // title="Shifts With Empty Rate Values Will Be Removed. Are You Sure To Proceed?"
                      description={
                        <>
                          <div className="max-w-[300px] p-4">
                            <p className="text-[16px] font-semibold leading-[normal] text-black">
                              Shifts With Empty Rate Values Will Be Removed. Are
                              You Sure To Proceed?
                            </p>
                          </div>
                        </>
                      }
                      okButtonProps={{
                        className:
                          "bg-primary text-white border !border-primary hover:!bg-white hover:!text-primary",
                      }}
                      cancelButtonProps={{
                        className:
                          "bg-white text-primary border !border-primary hover:!bg-primary hover:!text-white",
                      }}
                      okText="Yes"
                      cancelText="No"
                      icon={null}
                      disabled={
                        !jobShiftObj.some((item) => {
                          return !item.isRateFound;
                        })
                      }
                      onConfirm={() => {
                        if (createJobLoading) return;
                        setCreateJobLoading(true);
                        handleCreateJob();
                      }}
                    >
                      <button
                        className="ease relative h-[40px] w-[120px] rounded-lg border border-primary bg-primary text-[14px] font-medium leading-[38px] text-white transition-all duration-200 hover:bg-none hover:text-primary"
                        onClick={() => {
                          if (
                            !jobShiftObj.some((item) => {
                              return !item.isRateFound;
                            })
                          ) {
                            if (createJobLoading) return;
                            setCreateJobLoading(true);
                            handleCreateJob();
                            setTimeout(() => {
                              dispatch(deSelectAllCandidate());
                            }, 300);
                          }
                        }}
                        type="button"
                        disabled={
                          createJobLoading ||
                          getDayWiseApiLoading > 0 ||
                          jobShiftObj.length === 0
                        }
                      >
                        {createJobLoading ? (
                          <ButtonLoader />
                        ) : (
                          <span>Submit</span>
                        )}
                      </button>
                    </Popconfirm>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <Summary />
        </div>
        {jobData.totalDays > 0 && jobShiftObj.length ? <JobShiftLists /> : null}
      </div>
    </div>
  );
};

export default Verification;
